'use client';

import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { publicThemePalette } from '../../theme/publicTheme';

const adminTheme = createTheme({
    fontFamily: "'Inter', 'DM Sans', 'Sora', system-ui, -apple-system, sans-serif",
    fontSizes: {
        xs: '11px',
        sm: '12px',
        md: '14px',
        lg: '16px',
        xl: '18px',
    },
    headings: {
        fontFamily: "'Inter', 'DM Sans', 'Sora', system-ui, -apple-system, sans-serif",
        fontWeight: '600',
        sizes: {
            h1: { fontSize: '34px', lineHeight: '1.2' },
            h2: { fontSize: '26px', lineHeight: '1.25' },
            h3: { fontSize: '22px', lineHeight: '1.3' },
            h4: { fontSize: '19px', lineHeight: '1.3' },
            h5: { fontSize: '17px', lineHeight: '1.3' },
            h6: { fontSize: '14px', lineHeight: '1.3' },
        },
    },
    primaryColor: 'brand',
    primaryShade: 7,
    colors: {
        brand: [
            '#e8effa',
            '#d5e1f5',
            '#adc4eb',
            '#84a8e1',
            '#5f8dd8',
            '#3f74ce',
            '#2f5cb5',
            '#25498f',
            '#1c376b',
            '#132547',
        ],
        logo: [
            '#e6f7ff',
            '#c8ecfd',
            '#9fdefa',
            '#71cff7',
            '#47c1f3',
            '#29b6ef',
            '#16a8e0',
            '#0f8fbe',
            '#0f7297',
            '#105775',
        ],
        gray: [
            '#f7f9fc',
            '#f0f4fa',
            '#e3eaf4',
            '#ccd8e8',
            '#aebfd6',
            '#8a9db8',
            '#6a7f9e',
            '#506481',
            '#394b67',
            '#26354d',
        ],
        // Keep "blue" alias aligned with the brand scale for existing admin controls using color="blue".
        blue: [
            '#e8effa',
            '#d5e1f5',
            '#adc4eb',
            '#84a8e1',
            '#5f8dd8',
            '#3f74ce',
            '#2f5cb5',
            '#25498f',
            '#1c376b',
            '#132547',
        ],
    },
    components: {
        Button: {
            defaultProps: { size: 'sm', radius: 'md' },
        },
        TextInput: {
            defaultProps: { size: 'sm', radius: 'md', variant: 'filled' },
        },
        Select: {
            defaultProps: { size: 'sm', radius: 'md', variant: 'filled' },
        },
        Autocomplete: {
            defaultProps: { size: 'sm', radius: 'md', variant: 'filled' },
        },
        MultiSelect: {
            defaultProps: { size: 'sm', radius: 'md', variant: 'filled' },
        },
        Textarea: {
            defaultProps: { size: 'sm', radius: 'md', variant: 'filled', resize: 'vertical' },
        },
        Pagination: {
            defaultProps: { size: 'sm', radius: 'xl', color: 'gray' },
        },
        Paper: {
            defaultProps: { radius: 'md' },
        },
    },
});

export function AdminMantineProvider({ children }: { children: React.ReactNode }) {
    return (
        <MantineProvider theme={adminTheme}>
            <style>{`
                :root {
                    --admin-brand-primary: ${publicThemePalette.darkBlue};
                    --admin-brand-logo: ${publicThemePalette.logoBlue};
                    --admin-surface-soft: ${publicThemePalette.lightGray};
                    --admin-border: ${publicThemePalette.border};
                    --admin-text: ${publicThemePalette.textPrimary};
                    --admin-text-muted: ${publicThemePalette.textSecondary};
                }
            `}</style>
            <Notifications position="top-right" />
            {children}
        </MantineProvider>
    );
}
